import {
    type CollisionDetection,
    closestCenter,
    type DragEndEvent,
    type DragMoveEvent,
    type DragStartEvent,
    KeyboardSensor,
    PointerSensor,
    useSensor,
    useSensors,
} from "@dnd-kit/core";
import { sortableKeyboardCoordinates } from "@dnd-kit/sortable";
import { BuiltInAttribute } from "@orsane/schema/tasks";
import { useCallback, useRef, useState } from "react";
import { TASK_ROW_HEIGHT } from "@/common/constants";
import { useUpdateTaskListPreferencesMutation } from "@/mutations/task-list-mutations";
import { useChangeTaskParentMutation } from "@/mutations/task-mutations";
import type { TaskListPreferences, TaskQueryParams } from "@/types/list";
import type { ITableRow, Row } from "./RowModel";

type UseManualSortParams = {
    preferences: TaskListPreferences;
    queryParams: TaskQueryParams;
    sortColumnId: string;
    /**
     * Reads the current selection. Passed as a getter rather than a value so the
     * table doesn't have to subscribe to selection state just to start a drag —
     * the drag handlers only need it at the moment the drag begins.
     */
    getSelectedTaskIds: () => Record<number, boolean>;
    /** When true, disables nesting (drop-to-create-parent) but keeps reorder drag-and-drop */
    disableNesting?: boolean;
    clearSelection?: () => void;
};

export type DropIntent = "reorder" | "nest";
export type ReorderPosition = "above" | "below";

/**
 * Extract task IDs from flatRows for top-level tasks and subtask ordering.
 */
function snapshotOrder(flatRows: ITableRow[]) {
    const topLevelOrder: number[] = [];
    const subtaskOrder: Record<number, number[]> = {};

    for (const item of flatRows) {
        if (item.isGroup()) continue;
        const row = item as Row;
        if (row.data.id < 0) continue; // skip temp tasks

        if (row.depth === 0) {
            topLevelOrder.push(row.data.id);
        } else if (row.data.parentTask) {
            const parentId = row.data.parentTask.id;
            if (!subtaskOrder[parentId]) {
                subtaskOrder[parentId] = [];
            }
            subtaskOrder[parentId].push(row.data.id);
        }
    }

    return { topLevelOrder, subtaskOrder };
}

export function useManualSort({
    preferences,
    queryParams,
    sortColumnId,
    getSelectedTaskIds,
    disableNesting,
    clearSelection,
}: UseManualSortParams) {
    const [activeId, setActiveId] = useState<string | null>(null);
    const [draggedIds, setDraggedIds] = useState<Set<number>>(new Set());
    const [dropIntent, setDropIntent] = useState<DropIntent>("reorder");
    const [overTaskId, setOverTaskId] = useState<number | null>(null);
    const [reorderPosition, setReorderPosition] = useState<ReorderPosition>("below");
    const [dropTargetDepth, setDropTargetDepth] = useState<number>(0);
    const [lastNestTargetId, setLastNestTargetId] = useState<number | null>(null);
    const { mutateAsync: updatePreferences } = useUpdateTaskListPreferencesMutation();
    const changeParentMutation = useChangeTaskParentMutation();

    // Use refs so handleDragEnd always reads the latest values
    const dropIntentRef = useRef<DropIntent>("reorder");
    const draggedIdsRef = useRef<Set<number>>(new Set());
    const reorderPositionRef = useRef<ReorderPosition>("below");
    const dropTargetDepthRef = useRef<number>(0);

    // Kept in a ref so the drag callbacks stay stable while still reading the
    // latest getter.
    const getSelectedTaskIdsRef = useRef(getSelectedTaskIds);
    getSelectedTaskIdsRef.current = getSelectedTaskIds;

    const isManualSort = sortColumnId === BuiltInAttribute.manual;

    // Local state is the source of truth for rendering — updated synchronously on drop.
    // Preferences are persisted to the server in the background.
    const prefsOrder: number[] = (preferences["taskList.manualOrder"] as number[]) || [];
    const prefsSubtaskOrder: Record<number, number[]> =
        (preferences["taskList.manualSubtaskOrder"] as Record<number, number[]>) || {};

    const [localOrder, setLocalOrder] = useState<number[]>(prefsOrder);
    const [localSubtaskOrder, setLocalSubtaskOrder] = useState<Record<number, number[]>>(prefsSubtaskOrder);

    // Ref for flatRows — set by the parent after computing flatRows.
    const flatRowsRef = useRef<ITableRow[]>([]);
    const setFlatRowsRef = useCallback((rows: ITableRow[]) => {
        flatRowsRef.current = rows;
    }, []);

    // Sync local state when preferences change externally (page load, list switch, etc.)
    if (prefsSubtaskOrder !== localSubtaskOrder) {
        setLocalSubtaskOrder(prefsSubtaskOrder);
    }
    if (prefsOrder !== localOrder) {
        setLocalOrder(prefsOrder);
    }

    const sensors = useSensors(
        useSensor(PointerSensor, {
            activationConstraint: {
                distance: 5,
            },
        }),
        useSensor(KeyboardSensor, {
            coordinateGetter: sortableKeyboardCoordinates,
        })
    );

    const persistPreferences = useCallback(
        (newPreferences: TaskListPreferences) => {
            updatePreferences({
                type: queryParams.type,
                taskListId: queryParams.taskListId,
                parentTaskId: queryParams.parentTaskId,
                preferences: newPreferences,
            });
        },
        [updatePreferences, queryParams]
    );

    const ensureManualOrder = useCallback((): {
        order: number[];
        subtaskOrder: Record<number, number[]>;
        prefs: TaskListPreferences;
    } => {
        let currentOrder = localOrder;
        let currentSubtaskOrder = localSubtaskOrder;
        let prefs = preferences;
        let changed = false;

        if (currentOrder.length === 0) {
            const snapshot = snapshotOrder(flatRowsRef.current);
            currentOrder = snapshot.topLevelOrder;
            currentSubtaskOrder = snapshot.subtaskOrder;
            changed = true;
        } else {
            // Reconcile: append any tasks from flatRows that aren't in the order yet
            // (e.g. tasks created after the manual order was established)
            const snapshot = snapshotOrder(flatRowsRef.current);
            const orderSet = new Set(currentOrder);
            const missingTopLevel = snapshot.topLevelOrder.filter((id) => !orderSet.has(id));
            if (missingTopLevel.length > 0) {
                currentOrder = [...currentOrder, ...missingTopLevel];
                changed = true;
            }
            for (const [parentId, childIds] of Object.entries(snapshot.subtaskOrder)) {
                const existing = currentSubtaskOrder[Number(parentId)] || [];
                const existingSet = new Set(existing);
                const missingChildren = childIds.filter((id) => !existingSet.has(id));
                if (missingChildren.length > 0) {
                    currentSubtaskOrder = {
                        ...currentSubtaskOrder,
                        [Number(parentId)]: [...existing, ...missingChildren],
                    };
                    changed = true;
                }
            }
        }

        if (changed) {
            setLocalOrder(currentOrder);
            setLocalSubtaskOrder(currentSubtaskOrder);
            prefs = {
                ...preferences,
                "taskList.sorting": BuiltInAttribute.manual,
                "taskList.manualOrder": currentOrder,
                "taskList.manualSubtaskOrder": currentSubtaskOrder,
            };
        } else if (!isManualSort) {
            prefs = {
                ...preferences,
                "taskList.sorting": BuiltInAttribute.manual,
            };
        }

        return { order: currentOrder, subtaskOrder: currentSubtaskOrder, prefs };
    }, [localOrder, localSubtaskOrder, preferences, isManualSort]);

    const handleDragStart = useCallback(
        (event: DragStartEvent) => {
            const activeTaskId = Number(event.active.id);
            setActiveId(event.active.id.toString());
            setDropIntent("reorder");
            dropIntentRef.current = "reorder";
            setOverTaskId(null);

            // Only drag the full selection if the dragged task is part of it;
            // otherwise drag just the single task and clear the stale selection.
            const sel = getSelectedTaskIdsRef.current();
            const ids = new Set<number>();
            ids.add(activeTaskId);
            if (sel[activeTaskId]) {
                for (const [id, selected] of Object.entries(sel)) {
                    if (selected) ids.add(Number(id));
                }
            } else {
                clearSelection?.();
            }
            draggedIdsRef.current = ids;
            setDraggedIds(ids);

            if (!isManualSort) {
                const { prefs } = ensureManualOrder();
                persistPreferences(prefs);
            }
        },
        [isManualSort, ensureManualOrder, persistPreferences]
    );

    /**
     * Track drop intent during drag by measuring cursor position relative to the hovered row.
     * Center zone (20%-80%) = nest, edge zones = reorder.
     * For the last subtask of a parent, the bottom zone is split:
     *   20%-65% = reorder below as subtask sibling, 65%-100% = reorder below as top-level.
     */
    const handleDragMove = useCallback((event: DragMoveEvent) => {
        if (!event.over) {
            setDropIntent("reorder");
            dropIntentRef.current = "reorder";
            setOverTaskId(null);
            return;
        }

        const overId = Number(event.over.id);
        const activeTaskId = Number(event.active.id);

        // Don't allow nesting onto self, other dragged tasks, or temp tasks
        if (overId === activeTaskId || overId < 0 || draggedIds.has(overId)) {
            setDropIntent("reorder");
            dropIntentRef.current = "reorder";
            setOverTaskId(null);
            return;
        }

        const overRect = event.over.rect;
        const translated = event.active.rect.current.translated;
        if (!translated) {
            setDropIntent("reorder");
            dropIntentRef.current = "reorder";
            setOverTaskId(null);
            return;
        }

        // Calculate where the center of the dragged element is relative to the over element
        const dragCenterY = translated.top + TASK_ROW_HEIGHT / 2;
        const relativeY = dragCenterY - overRect.top;
        const ratio = relativeY / overRect.height;

        const flatRows = flatRowsRef.current;

        // Prevent nesting more than 1 level deep:
        // - Don't nest onto a task that is already a subtask (target depth > 0)
        // - Don't nest tasks that already have subtasks (their children would become depth 2+)
        const overRowIndex = flatRows.findIndex((r) => !r.isGroup() && (r as Row).data.id === overId);
        const overRow = overRowIndex >= 0 ? (flatRows[overRowIndex] as Row) : undefined;
        const isOverAlreadySubtask = overRow ? overRow.depth > 0 : false;
        const anyDraggedHasSubtasks = Array.from(draggedIdsRef.current).some((id) => {
            const row = flatRows.find((r) => !r.isGroup() && (r as Row).data.id === id) as Row | undefined;
            return row?.hasSubtasks;
        });
        const canNest = !disableNesting && !isOverAlreadySubtask && !anyDraggedHasSubtasks;

        // Check if hovering over the last subtask at the end of a section — enables
        // a bottom zone where dropping places the task at top-level after the parent.
        // Only active when there are no more rows below in the section (end of table
        // or next row is a group header), since otherwise the user can target the
        // next top-level row directly.
        let isLastSubtaskInSection = false;
        if (overRow && overRow.depth > 0) {
            const nextRow = overRowIndex + 1 < flatRows.length ? flatRows[overRowIndex + 1] : undefined;
            isLastSubtaskInSection = !nextRow || nextRow.isGroup();
        }

        if (canNest && ratio > 0.2 && ratio < 0.8) {
            setDropIntent("nest");
            dropIntentRef.current = "nest";
            setOverTaskId(overId);
            dropTargetDepthRef.current = overRow?.depth ?? 0;
            setDropTargetDepth(overRow?.depth ?? 0);
        } else if (isLastSubtaskInSection && ratio > 0.65) {
            // Bottom zone of the last subtask → drop as top-level after the parent
            setDropIntent("reorder");
            dropIntentRef.current = "reorder";
            setOverTaskId(overId);
            setReorderPosition("below");
            reorderPositionRef.current = "below";
            dropTargetDepthRef.current = 0;
            setDropTargetDepth(0);
        } else {
            setDropIntent("reorder");
            dropIntentRef.current = "reorder";
            setOverTaskId(overId);
            const pos = ratio <= 0.2 ? "above" : "below";
            setReorderPosition(pos);
            reorderPositionRef.current = pos;
            dropTargetDepthRef.current = overRow?.depth ?? 0;
            setDropTargetDepth(overRow?.depth ?? 0);
        }
    }, []);

    const handleDragEnd = useCallback(
        (event: DragEndEvent) => {
            const currentDropIntent = dropIntentRef.current;
            const currentDraggedIds = draggedIdsRef.current;
            const currentReorderPosition = reorderPositionRef.current;
            const currentDropTargetDepth = dropTargetDepthRef.current;

            // Reset drag state
            setActiveId(null);
            draggedIdsRef.current = new Set();
            setDraggedIds(new Set());
            setDropIntent("reorder");
            dropIntentRef.current = "reorder";
            setOverTaskId(null);
            dropTargetDepthRef.current = 0;
            setDropTargetDepth(0);

            if (!event.active || !event.over) return;
            if (event.active.id === event.over.id && currentDraggedIds.size <= 1) return;

            const overTaskIdValue = Number(event.over.id);
            const flatRows = flatRowsRef.current;

            // Resolve all dragged rows (preserving visual order from flatRows)
            const allDraggedRows = flatRows.filter(
                (r) => !r.isGroup() && currentDraggedIds.has((r as Row).data.id)
            ) as Row[];
            if (allDraggedRows.length === 0) return;

            const overRow = flatRows.find((r) => !r.isGroup() && (r as Row).data.id === overTaskIdValue) as
                | Row
                | undefined;
            if (!overRow) return;

            // Don't drop onto one of the dragged tasks
            if (currentDraggedIds.has(overTaskIdValue)) return;

            const { order, subtaskOrder, prefs } = ensureManualOrder();
            const allDraggedIds = new Set(allDraggedRows.map((r) => r.data.id));

            console.log("[DragDrop BEFORE]", {
                manualOrder: [...order],
                manualSubtaskOrder: JSON.parse(JSON.stringify(subtaskOrder)),
            });

            // ─── Case 0: Nest — drop onto a task to make all dragged tasks children ───
            if (currentDropIntent === "nest") {
                const newParentId = overTaskIdValue;
                let newOrder = [...order];
                const newSubOrder = { ...subtaskOrder };

                // Remove all dragged tasks from their current positions
                for (const row of allDraggedRows) {
                    const parentId = row.data.parentTask?.id ?? null;
                    if (parentId !== null) {
                        newSubOrder[parentId] = (newSubOrder[parentId] || []).filter((id) => !allDraggedIds.has(id));
                    } else {
                        newOrder = newOrder.filter((id) => !allDraggedIds.has(id));
                    }
                }

                // Add all to new parent's subtask order
                if (!newSubOrder[newParentId]) {
                    newSubOrder[newParentId] = [];
                }
                for (const row of allDraggedRows) {
                    newSubOrder[newParentId].push(row.data.id);
                }

                setLocalOrder(newOrder);
                setLocalSubtaskOrder(newSubOrder);
                console.log("[DragDrop AFTER nest]", {
                    manualOrder: [...newOrder],
                    manualSubtaskOrder: JSON.parse(JSON.stringify(newSubOrder)),
                });
                persistPreferences({
                    ...prefs,
                    "taskList.manualOrder": newOrder,
                    "taskList.manualSubtaskOrder": newSubOrder,
                });

                // Change parent on the backend for each dragged task
                const parentTask = {
                    id: overRow.data.id,
                    number: overRow.data.number,
                    title: overRow.data.title,
                    key: overRow.data.key,
                };
                for (const row of allDraggedRows) {
                    changeParentMutation.mutate({ id: row.data.id, parentTask });
                }

                setLastNestTargetId(newParentId);
                return;
            }

            // ─── Reorder — remove all dragged tasks, then insert at the drop position ───
            let newOrder = [...order];
            const newSubOrder = { ...subtaskOrder };

            // Step 1: Remove all dragged tasks from wherever they currently are
            for (const row of allDraggedRows) {
                const pid = row.data.parentTask?.id ?? null;
                if (pid !== null) {
                    newSubOrder[pid] = (newSubOrder[pid] || []).filter((id) => !allDraggedIds.has(id));
                }
            }
            newOrder = newOrder.filter((id) => !allDraggedIds.has(id));

            const allDraggedIdsOrdered = allDraggedRows.map((r) => r.data.id);
            const overParentId = overRow.data.parentTask?.id ?? null;

            // Step 2: Insert at the drop target.
            // Use currentDropTargetDepth (not overRow.depth) — when dragging to the
            // bottom zone of the last subtask, depth is 0 even though overRow is a subtask.
            if (currentDropTargetDepth === 0) {
                // Dropping as top-level
                if (overRow.depth > 0 && overParentId !== null) {
                    // Over row is a subtask but we're dropping as top-level after the parent
                    const parentIndex = newOrder.indexOf(overParentId);
                    const insertAt = parentIndex !== -1 ? parentIndex + 1 : newOrder.length;
                    newOrder.splice(insertAt, 0, ...allDraggedIdsOrdered);
                } else {
                    const overIndex = newOrder.indexOf(overTaskIdValue);
                    if (overIndex !== -1) {
                        const insertAt = currentReorderPosition === "below" ? overIndex + 1 : overIndex;
                        newOrder.splice(insertAt, 0, ...allDraggedIdsOrdered);
                    } else {
                        newOrder.push(...allDraggedIdsOrdered);
                    }
                }

                // Un-parent any subtasks in the selection
                for (const row of allDraggedRows) {
                    if (row.data.parentTask) {
                        changeParentMutation.mutate({ id: row.data.id, parentTask: null });
                    }
                }
            } else if (overParentId !== null) {
                // Dropping on a subtask → all become siblings under that parent
                const parentOrder = (newSubOrder[overParentId] || []).filter((id) => !allDraggedIds.has(id));
                const overIdx = parentOrder.indexOf(overTaskIdValue);
                if (overIdx !== -1) {
                    const insertAt = currentReorderPosition === "below" ? overIdx + 1 : overIdx;
                    parentOrder.splice(insertAt, 0, ...allDraggedIdsOrdered);
                } else {
                    parentOrder.push(...allDraggedIdsOrdered);
                }
                newSubOrder[overParentId] = parentOrder;

                // Re-parent tasks that had a different parent (or no parent)
                const overParentRow = flatRows.find((r) => !r.isGroup() && (r as Row).data.id === overParentId) as
                    | Row
                    | undefined;
                for (const row of allDraggedRows) {
                    const currentParentId = row.data.parentTask?.id ?? null;
                    if (currentParentId !== overParentId && overParentRow) {
                        changeParentMutation.mutate({
                            id: row.data.id,
                            parentTask: {
                                id: overParentId,
                                number: overParentRow.data.number,
                                title: overParentRow.data.title,
                                key: overParentRow.data.key,
                            },
                        });
                    }
                }
            }

            setLocalOrder(newOrder);
            setLocalSubtaskOrder(newSubOrder);
            console.log("[DragDrop AFTER reorder]", {
                manualOrder: [...newOrder],
                manualSubtaskOrder: JSON.parse(JSON.stringify(newSubOrder)),
            });
            persistPreferences({
                ...prefs,
                "taskList.manualOrder": newOrder,
                "taskList.manualSubtaskOrder": newSubOrder,
            });
        },
        [ensureManualOrder, persistPreferences, changeParentMutation]
    );

    const handleDragCancel = useCallback(() => {
        setActiveId(null);
        draggedIdsRef.current = new Set();
        setDraggedIds(new Set());
        setDropIntent("reorder");
        dropIntentRef.current = "reorder";
        setOverTaskId(null);
        setReorderPosition("below");
        dropTargetDepthRef.current = 0;
        setDropTargetDepth(0);
    }, []);

    const collisionDetection: CollisionDetection = closestCenter;
    const modifiers: [] = [];

    return {
        sensors,
        activeId,
        draggedIds,
        dropIntent,
        overTaskId,
        reorderPosition,
        dropTargetDepth,
        lastNestTargetId,
        handleDragStart,
        handleDragMove,
        handleDragEnd,
        handleDragCancel,
        collisionDetection,
        modifiers,
        manualOrder: localOrder,
        manualSubtaskOrder: localSubtaskOrder,
        setFlatRowsRef,
    };
}
